// src/pages/Private/Student/StudentDashboard.js
import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchStudentCourses, fetchUpcomingLessons, fetchAllLessons } from '../../../services/studentService';
import { Link } from 'react-router-dom';
import './StudentDashboard.css';

function StudentDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalLessons: 0,
    upcomingLessons: 0,
    completedLessons: 0,
    pendingAssignments: 0,
    pendingQuizzes: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [coursesData, lessonsData, allLessonsData] = await Promise.all([
        fetchStudentCourses(),
        fetchUpcomingLessons(),
        fetchAllLessons()
      ]);

      setCourses(coursesData);
      setLessons(lessonsData);
      setAllLessons(allLessonsData);
      calculateStats(coursesData, allLessonsData, lessonsData);
    } catch (err) {
      setError('Error loading data: ' + (err.response?.data?.msg || err.message));
      console.error('Dashboard yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (coursesData, allLessonsData, upcomingLessonsData) => {
    const now = new Date();
    
    const completedLessons = allLessonsData.filter(lesson => 
      new Date(lesson.scheduledAt) <= now
    ).length;

    setStats({
      totalCourses: coursesData.length,
      totalLessons: allLessonsData.length,
      upcomingLessons: upcomingLessonsData.length,
      completedLessons,
      pendingAssignments: 2,
      pendingQuizzes: 1
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const lessonDate = new Date(dateString);
    const diffMs = lessonDate - now;
    
    if (diffMs <= 0) return 'Started';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    if (diffHours > 0) return `${diffHours}h ${diffMins}m`;
    return `${diffMins}m`;
  };

  const calculateProgress = () => {
    if (stats.totalLessons === 0) return 0;
    return Math.round((stats.completedLessons / stats.totalLessons) * 100);
  };

  if (loading) {
    return (
      <div className="student-dashboard-loading">
        <div className="loading-spinner">📚</div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const upcomingLessons = lessons.slice(0, 3);

  return (
    <div className="student-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>🎓 Welcome, {user?.name || 'Student'}!</h1>
          <p>Wishing you success in your learning journey</p>
        </div>
        <button onClick={loadDashboardData} className="refresh-btn" disabled={loading}>
          <span className="refresh-icon">🔄</span>
          Refresh
        </button>
      </div>

      {error && (
        <div className="dashboard-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <h3>Total Courses</h3>
            <div className="stat-value">{stats.totalCourses}</div>
            <div className="stat-change">Your active courses</div>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>Total Lessons</h3>
            <div className="stat-value">{stats.totalLessons}</div>
            <div className="stat-change">Total lessons</div>
          </div>
        </div>
        
        <div className="stat-card warning">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>Upcoming Lessons</h3>
            <div className="stat-value">{stats.upcomingLessons}</div>
            <div className="stat-change">Scheduled lessons</div>
          </div>
        </div>
        
        <div className="stat-card info">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Completed</h3>
            <div className="stat-value">{stats.completedLessons}</div>
            <div className="stat-change">Lessons completed</div>
          </div>
        </div>
      </div>

      {/* Pending Tasks */}
      {(stats.pendingQuizzes > 0 || stats.pendingAssignments > 0) && (
        <div className="pending-tasks">
          <h3>📋 Pending Tasks</h3>
          <div className="tasks-grid">
            {stats.pendingQuizzes > 0 && (
              <Link to="/student/lessons" className="task-card">
                <span className="task-icon">📝</span>
                <span className="task-count">{stats.pendingQuizzes}</span>
                <span className="task-label">Quizzes to Take</span>
              </Link>
            )}
            {stats.pendingAssignments > 0 && (
              <Link to="/student/lessons" className="task-card">
                <span className="task-icon">📋</span>
                <span className="task-count">{stats.pendingAssignments}</span>
                <span className="task-label">Assignments to Submit</span>
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {/* Left Column - Upcoming Lessons */}
        <div className="content-column">
          <div className="content-card">
            <div className="card-header">
              <h3>⏰ Upcoming Lessons</h3>
              <Link to="/student/lessons" className="view-all">View All →</Link>
            </div>
            
            {upcomingLessons.length === 0 ? (
              <div className="empty-state">
                <span>📅</span>
                <p>No upcoming lessons</p>
                <p className="empty-subtitle">Check your lesson schedule</p>
              </div>
            ) : (
              <div className="upcoming-lessons">
                {upcomingLessons.map(lesson => (
                  <div key={lesson._id} className="lesson-card">
                    <div className="lesson-info">
                      <h4>{lesson.title}</h4>
                      <p className="course-name">{lesson.course?.title}</p>
                      <p className="lesson-time">{formatDate(lesson.scheduledAt)}</p>
                      <p className="lesson-duration">{lesson.durationMinutes} minutes</p>
                    </div>
                    
                    <div className="lesson-actions">
                      <span className="time-until">{getTimeUntil(lesson.scheduledAt)}</span>
                      {lesson.zoomLink && (
                        <a 
                          href={lesson.zoomLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="join-btn"
                        >
                          Join
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Access */}
          <div className="content-card">
            <h3>⚡ Quick Access</h3>
            <div className="quick-actions">
              <Link to="/student/courses" className="quick-action">
                <span className="action-icon">📚</span>
                <span className="action-text">My Courses</span>
              </Link>
              <Link to="/student/lessons" className="quick-action">
                <span className="action-icon">⏰</span>
                <span className="action-text">Lesson Schedule</span>
              </Link>
              <Link to="/student/certificates" className="quick-action">
                <span className="action-icon">🏆</span>
                <span className="action-text">My Certificates</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column - My Courses */}
        <div className="content-column">
          <div className="content-card">
            <div className="card-header">
              <h3>📚 My Courses</h3>
              <Link to="/student/courses" className="view-all">View All →</Link>
            </div>
            
            {courses.length === 0 ? (
              <div className="empty-state">
                <span>📖</span>
                <p>You are not enrolled in any courses</p>
                <p className="empty-subtitle">Enroll in courses to start learning</p>
              </div>
            ) : (
              <div className="courses-grid">
                {courses.slice(0, 3).map(course => (
                  <div key={course._id} className="course-card">
                    <div className="course-image">
                      {course.imageUrl ? (
                        <img src={course.imageUrl} alt={course.title} />
                      ) : (
                        <div className="course-image-placeholder">📚</div>
                      )}
                    </div>
                    
                    <div className="course-content">
                      <h4>{course.title}</h4>
                      <p className="course-description">
                        {course.description?.length > 100 
                          ? `${course.description.substring(0, 100)}...` 
                          : course.description
                        }
                      </p>
                      
                      <div className="course-meta">
                        <span className="meta-item">
                          👨‍🏫 {course.teacher?.name || 'Instructor'}
                        </span>
                      </div>

                      <Link 
                        to={`/student/course/${course._id}`}
                        className="course-link"
                      >
                        Go to Course
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Learning Progress */}
          <div className="content-card">
            <h3>📊 Learning Progress</h3>
            <div className="progress-section">
              <div className="progress-item">
                <span>Overall Progress</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
                <span>%{calculateProgress()}</span>
              </div>
              
              <div className="progress-item">
                <span>Lesson Attendance</span>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${stats.totalLessons > 0 ? (stats.completedLessons / stats.totalLessons) * 100 : 0}%` }}
                  ></div>
                </div>
                <span>%{Math.round((stats.completedLessons / (stats.totalLessons || 1)) * 100)}</span>
              </div>

              <div className="progress-stats">
                <div className="progress-stat">
                  <span className="stat-label">Completed Lessons</span>
                  <span className="stat-value">{stats.completedLessons} / {stats.totalLessons}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="content-card">
            <h3>💡 Tips</h3>
            <ul className="tips-list">
              <li>Complete lessons to increase your progress</li>
              <li>Take quizzes to test your knowledge</li>
              <li>Submit assignments on time</li>
              <li>Earn certificates by completing courses</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;